import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, PLATFORM_ID, inject, ViewChild } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CommonModule } from '@angular/common';
import { TipoTicket, TipoTicketDetailed, TicketsService, Precio } from '../../core/services/tickets.service';
import { CartService } from '../../core/services/cart.service';
import { GuestSession } from '../../core/services/guest-session';
import { Subscription, firstValueFrom } from 'rxjs';
import { AppSyncService } from '../../core/services/app-sync.service';
import { Event } from '../../core/services/events.service';
import { SeatMapComponent } from '../seat-map/seat-map.component';
import { TicketSelector } from '../ticket-selector/ticket-selector';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-ticket-selector-tren',
  standalone: true,
  imports: [CommonModule, SeatMapComponent, TicketSelector],
  templateUrl: './ticket-selector-tren.html',
  styleUrl: './ticket-selector-tren.css',
})
export class TicketSelectorTren implements OnInit, OnChanges {
  @Input() eventoid?: string;
  @Input() url_slug?: string;
  @Input() event?: Event;
  /** Modo directo: va directo al mapa de asientos o selector normal */
  @Input() directTipoTicketId?: string;
  @Output() ticketsAdded = new EventEmitter<void>();

  @ViewChild(SeatMapComponent) seatMapComponent?: SeatMapComponent;

  private platformId = inject(PLATFORM_ID);
  private isBrowser: boolean;

  isLoading = true;
  error = false;

  vagonDetalladoSeleccionado?: TipoTicketDetailed;
  precioSeleccionado?: Precio;
  isLoadingDetalle = false;

  // Estado del modal
  showPriceModal = false;
  selectedSeatForModal?: any;
  isAddingToCart = false;

  private disponibilidadSubscription?: Subscription;
  private ticketsByEstadoCounts: Record<string, number> = {
    disponible: 0,
    reservado: 0,
    vendido: 0,
    validado: 0,
    anulado: 0,
    cortesia: 0,
    bloqueado: 0,
  };
  private hasEstadoSnapshot = false;

  constructor(
    private ticketsService: TicketsService,
    private cartService: CartService,
    private guestSession: GuestSession,
    private cdr: ChangeDetectorRef,
    private appSyncService: AppSyncService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    if (this.directTipoTicketId) {
      this.isLoading = false;
      this.isLoadingDetalle = true;
      this.ticketsService.getTipoTicketByIdForWeb(this.directTipoTicketId).subscribe({
        next: (response) => {
          if (response.success) {
            setTimeout(() => {
              this.vagonDetalladoSeleccionado = response.data;
              this.hydrateCountsFromDetailedTickets(this.vagonDetalladoSeleccionado);
              this.isLoadingDetalle = false;
              if (this.vagonDetalladoSeleccionado) {
                void this.BuscarEstadosTickets(this.vagonDetalladoSeleccionado);
              }
              this.cdr.markForCheck();
            }, 0);
          } else {
            this.isLoadingDetalle = false;
            this.cdr.markForCheck();
          }
        },
        error: () => {
          this.isLoadingDetalle = false;
          this.cdr.markForCheck();
        }
      });
      if (this.url_slug && this.isBrowser) {
        this.setupDisponibilidadSubscription(this.url_slug);
      }
    } else {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  ngOnChanges(_changes: SimpleChanges): void {}

  ngOnDestroy(): void {
    if (this.disponibilidadSubscription) {
      this.disponibilidadSubscription.unsubscribe();
    }
    this.appSyncService.unsubscribeFromDisponibilidad();
  }

  private setupDisponibilidadSubscription(urlSlug: string): void {
    if (!this.isBrowser) return;
    try {
      this.disponibilidadSubscription = this.appSyncService.disponibilidadChanged$.subscribe({
        next: (data) => {
          if (this.vagonDetalladoSeleccionado?.tipoticketid === data.tipoticketid) {
            this.vagonDetalladoSeleccionado.aforo_disponible = data.aforo_disponible;
            this.vagonDetalladoSeleccionado.tickets_reservados = data.tickets_reservados;
            this.vagonDetalladoSeleccionado.aforo_maximo = data.aforo_maximo;
            this.refreshTipoTicketSnapshot(data.tipoticketid);
            void this.BuscarEstadosTickets(this.vagonDetalladoSeleccionado);
            this.cdr.markForCheck();
          }
        },
        error: () => {}
      });
      this.appSyncService.subscribeToDisponibilidad(urlSlug);
    } catch {}
  }

  async BuscarEstadosTickets(tipoTicket: TipoTicket): Promise<void> {
    try {
      const response = await firstValueFrom(this.ticketsService.getTicketsByEstado(tipoTicket.tipoticketid));

      if (response.success && response.data) {
        const estados = response.data.tickets_by_estado;

        (['disponible', 'reservado', 'vendido', 'validado', 'anulado', 'cortesia', 'bloqueado'] as const)
          .forEach((estado) => {
            this.ticketsByEstadoCounts[estado] = (estados[estado] ?? []).length;
          });
        this.hasEstadoSnapshot = true;

        if (this.seatMapComponent) {
          const cartTicketIds = await this.getCartTicketIds();
          (['disponible', 'reservado', 'vendido', 'validado', 'anulado', 'cortesia', 'bloqueado'] as const).forEach(estado => {
            (estados[estado] ?? []).forEach(id => this.seatMapComponent!.updateSeatStatusByTicketId(id, estado, cartTicketIds.has(id)));
          });
        }

        this.cdr.markForCheck();
      }
    } catch {}
  }

  private refreshTipoTicketSnapshot(tipoticketid: string): void {
    this.ticketsService.getTipoTicketByIdForWeb(tipoticketid).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.vagonDetalladoSeleccionado = response.data;
          this.hydrateCountsFromDetailedTickets(response.data);
          this.cdr.markForCheck();
        }
      },
      error: () => {}
    });
  }

  private hydrateCountsFromDetailedTickets(tipoTicket?: TipoTicketDetailed): void {
    if (!tipoTicket || !Array.isArray(tipoTicket.tickets) || tipoTicket.tickets.length === 0) {
      return;
    }

    this.ticketsByEstadoCounts = {
      disponible: 0,
      reservado: 0,
      vendido: 0,
      validado: 0,
      anulado: 0,
      cortesia: 0,
      bloqueado: 0,
    };

    for (const ticket of tipoTicket.tickets) {
      const estado = String(ticket?.estado || '').toLowerCase();
      if (estado in this.ticketsByEstadoCounts) {
        this.ticketsByEstadoCounts[estado as keyof typeof this.ticketsByEstadoCounts] += 1;
      }
    }

    this.hasEstadoSnapshot = true;
  }

  getReservadosCount(): number {
    if (this.hasEstadoSnapshot) return this.ticketsByEstadoCounts['reservado'];
    return this.vagonDetalladoSeleccionado?.tickets_reservados || 0;
  }

  getBloqueadosCount(): number {
    if (!this.hasEstadoSnapshot) return 0;
    return this.ticketsByEstadoCounts['bloqueado'] || 0;
  }

  getDisponiblesCount(): number {
    if (!this.vagonDetalladoSeleccionado) return 0;

    if (this.hasEstadoSnapshot) {
      return this.ticketsByEstadoCounts['disponible'] || 0;
    }

    const base = Number(this.vagonDetalladoSeleccionado.aforo_disponible || 0);
    return Math.max(0, base - this.getReservadosCount() - this.getBloqueadosCount());
  }

  private async getCartTicketIds(): Promise<Set<string>> {
    if (!this.isBrowser) {
      return new Set<string>();
    }

    if (!this.guestSession.isSessionActive() && !this.guestSession.getSessionId()) {
      return new Set<string>();
    }

    try {
      const response = await firstValueFrom(this.cartService.getCartBySession(this.guestSession.getSessionId()));
      if (!response.success || !response.data) {
        return new Set<string>();
      }

      const cartTickets = response.data.ticketsDetailed?.map(ticket => ticket.ticketid)
        || response.data.tickets
        || [];

      return new Set(cartTickets);
    } catch {
      return new Set<string>();
    }
  }

  onSeatClicked(ticket: any): void {
    this.selectedSeatForModal = ticket;
    this.showPriceModal = true;
    this.precioSeleccionado = undefined;
  }

  closePriceModal(): void {
    this.showPriceModal = false;
    this.selectedSeatForModal = undefined;
    this.precioSeleccionado = undefined;
  }

  seleccionarPrecioEnModal(precio: Precio): void {
    this.precioSeleccionado = precio;
  }

  getPrecioId(precio: Precio | null | undefined): string {
    return this.ticketsService.getPrecioId(precio);
  }

  /**
   * Devuelve los precios del vagón seleccionado agrupados para el modal:
   * - Si todos son 'unico' (o no hay variación nacional/extranjero):
   *     [{ grupo: null, precios: [...ordenados por nombre] }]
   * - Si hay nacional y/o extranjero:
   *     [{ grupo: 'Nacional', precios: [...] }, { grupo: 'Extranjero', precios: [...] }]
   *   Solo se incluye el grupo si tiene al menos un precio.
   *   Cada grupo está ordenado por nombre_precio.
   */
  get preciosAgrupados(): { grupo: string | null; precios: Precio[] }[] {
    const precios = this.vagonDetalladoSeleccionado?.precios ?? [];
    if (precios.length === 0) return [];

    const tiposGrupo = new Set(
      precios.map(p => (p.tipoprecio ?? '').toLowerCase())
    );

    const tieneNacionalOExtranjero =
      tiposGrupo.has('nacional') || tiposGrupo.has('extranjero');

    const sortByNombre = (a: Precio, b: Precio) =>
      (a.nombre_precio ?? '').localeCompare(b.nombre_precio ?? '', 'es', { sensitivity: 'base' });

    if (!tieneNacionalOExtranjero) {
      return [{ grupo: null, precios: [...precios].sort(sortByNombre) }];
    }

    const nacional = precios
      .filter(p => (p.tipoprecio ?? '').toLowerCase() === 'nacional')
      .sort(sortByNombre);
    const extranjero = precios
      .filter(p => (p.tipoprecio ?? '').toLowerCase() === 'extranjero')
      .sort(sortByNombre);

    const grupos: { grupo: string | null; precios: Precio[] }[] = [];
    if (nacional.length > 0) grupos.push({ grupo: 'Nacional', precios: nacional });
    if (extranjero.length > 0) grupos.push({ grupo: 'Extranjero', precios: extranjero });
    return grupos;
  }

  async confirmarYAgregarAsiento(): Promise<void> {
    if (this.isAddingToCart) return;
    if (!this.selectedSeatForModal || !this.precioSeleccionado || !this.event) return;

    this.isAddingToCart = true;

    try {
      let sessionid = this.guestSession.getSessionId();
      if (!sessionid && !this.guestSession.isSessionActive()) {
        const sessionResponse = await this.guestSession.ensureSession().toPromise();
        if (!sessionResponse?.success) return;
        sessionid = this.guestSession.getSessionId();
      }

      // Verificar límite max_tickets_por_compra contra tickets ya en carrito
      const maxPorCompra = this.event.max_tickets_por_compra ?? 10;
      const ticketsEnCarrito = await firstValueFrom(this.cartService.getCartTicketCount(sessionid));
      if (ticketsEnCarrito >= maxPorCompra) {
        await Swal.fire({
          icon: 'warning',
          title: 'Límite de tickets alcanzado',
          html: `Solo puedes comprar <strong>${maxPorCompra}</strong> tickets por compra. Ya tienes <strong>${ticketsEnCarrito}</strong> en tu carrito.`,
          confirmButtonColor: '#e8343f',
        });
        this.closePriceModal();
        this.cdr.detectChanges();
        return;
      }

      // Verificar si el carrito ya tiene tickets de otro evento
      const conflict = await firstValueFrom(
        this.cartService.checkCartEventConflict(sessionid, this.eventoid!)
      );
      if (conflict) {
        const result = await Swal.fire({
          icon: 'warning',
          title: 'Carrito con otro evento',
          html: `No puedes agregar tickets de otro evento. Actualmente tienes tickets de <strong>"${conflict.eventName}"</strong> en tu carrito. Debes completar o eliminar ese carrito primero.`,
          showCancelButton: true,
          confirmButtonText: 'Eliminar carrito',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#e8343f',
          cancelButtonColor: '#6b7280',
          reverseButtons: true
        });
        if (result.isConfirmed) {
          await firstValueFrom(this.cartService.clearCart(conflict.cartid));
          this.cartService.notifyTicketsAdded();
        }
        this.closePriceModal();
        this.cdr.detectChanges();
        return;
      }

      const request = {
        sessionId: sessionid,
        tipoticketid: this.selectedSeatForModal.tipoticketid,
        ticketid: this.selectedSeatForModal.ticketid,
        precioId: this.getPrecioId(this.precioSeleccionado)
      };

      const response = await firstValueFrom(this.cartService.addTicket(request));

      if (!response.success) {
        Swal.fire({
          icon: 'warning',
          title: 'No se pudo agregar el ticket',
          text: response.message || 'Ocurrió un error inesperado',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#e8343f'
        });
        return;
      }

      if (this.seatMapComponent) {
        this.seatMapComponent.markSeatAsReserved(this.selectedSeatForModal.ticketid);
      }

      this.ticketsAdded.emit();
      this.cartService.notifyTicketsAdded();
      this.closePriceModal();
    } catch (error: any) {
      console.error('Error adding ticket:', error);
      const message = error?.error?.message || error?.message || 'Error al agregar el ticket al carrito';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: message,
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#e8343f'
      });
    } finally {
      this.isAddingToCart = false;
      this.cdr.detectChanges();
    }
  }
}

